import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  Typography,
  Checkbox,
  FormControlLabel,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  Alert,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Step,
  StepLabel,
  Stepper,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { useWarbandStore } from '../../stores/warbandStore';
import { useDirtyTrackingStore } from '../../stores/dirtyTrackingStore';
import { useReferenceDataStore } from '../../stores/referenceDataStore';
import { Armour, Trick, Weapon } from '../../types';
import {
  CAPTAIN_HIRE_COST,
  CAPTAIN_STAT_INCREASES,
  CAPTAIN_STARTING_TRICK_COUNT,
  CAPTAIN_WEAPON_PRICES,
  CAPTAIN_ARMOUR_PRICES,
  getCaptainEquipmentCost,
  getCaptainEquipmentWarnings,
} from '../../utils/captainRules';

const STEPS = ['Details', 'Weapons', 'Armour', 'Tricks'];

const priceLabel = (price: number) => (price === 0 ? 'Free' : `${price}gc`);

function weaponDetails(weapon: Weapon): string {
  const parts: string[] = [];
  if (weapon.damageModifier !== null && weapon.damageModifier !== 0) {
    parts.push(`Damage ${weapon.damageModifier > 0 ? '+' : ''}${weapon.damageModifier}`);
  }
  if (weapon.maxRange) parts.push(`Range ${weapon.maxRange}"`);
  if (weapon.notes) parts.push(weapon.notes);
  for (const tag of weapon.specialTags) {
    if (tag.description) parts.push(tag.description);
  }
  if (weapon.description) parts.push(weapon.description);
  return parts.join(' • ');
}

function armourDetails(armour: Armour): string {
  const parts: string[] = [];
  if (armour.armourModifier) {
    parts.push(`Armour ${armour.armourModifier > 0 ? '+' : ''}${armour.armourModifier}`);
  }
  if (armour.notes) parts.push(armour.notes);
  for (const tag of armour.specialTags) {
    if (tag.description) parts.push(tag.description);
  }
  if (armour.description) parts.push(armour.description);
  return parts.join(' • ');
}

interface SelectableItemProps {
  primary: string;
  secondary?: string;
  selected: boolean;
  disabled?: boolean;
  onClick: () => void;
}

const SelectableItem: React.FC<SelectableItemProps> = ({ primary, secondary, selected, disabled, onClick }) => (
  <ListItemButton selected={selected} disabled={disabled} onClick={onClick}>
    <ListItemIcon sx={{ minWidth: 36 }}>
      {selected ? <CheckCircleIcon color="primary" /> : <RadioButtonUncheckedIcon />}
    </ListItemIcon>
    <ListItemText primary={primary} secondary={secondary || undefined} />
  </ListItemButton>
);

export const CreateCaptainModal: React.FC = () => {
  const ui = useWarbandStore((s) => s.ui);
  const closeCreateCaptainModal = useWarbandStore((s) => s.closeCreateCaptainModal);
  const warbands = useWarbandStore((s) => s.warbands);
  const selectedWarbandId = useWarbandStore((s) => s.selectedWarbandId);
  const warband = warbands.find((w) => w.id === selectedWarbandId);

  const weapons = useReferenceDataStore((s) => s.weapons);
  const armours = useReferenceDataStore((s) => s.armours);
  const tricks = useReferenceDataStore((s) => s.tricks);

  const [activeStep, setActiveStep] = React.useState(0);
  const [name, setName] = React.useState('');
  const [nameError, setNameError] = React.useState<string | null>(null);
  const [statIncreaseId, setStatIncreaseId] = React.useState('fight');
  const [selectedWeaponIds, setSelectedWeaponIds] = React.useState<string[]>([]);
  const [selectedArmourIds, setSelectedArmourIds] = React.useState<string[]>([]);
  const [selectedTrickIds, setSelectedTrickIds] = React.useState<string[]>([]);
  const [isFree, setIsFree] = React.useState(false);

  const captainWeapons = weapons.filter((w) => w.id in CAPTAIN_WEAPON_PRICES);
  const captainArmours = armours.filter((a) => a.id in CAPTAIN_ARMOUR_PRICES);

  const equipmentCost = getCaptainEquipmentCost(selectedWeaponIds, selectedArmourIds);
  const totalCost = CAPTAIN_HIRE_COST + equipmentCost;
  const warnings = getCaptainEquipmentWarnings(selectedWeaponIds, selectedArmourIds);
  const insufficientGold = !isFree && !!warband && warband.gold < totalCost;

  const isLastStep = activeStep === STEPS.length - 1;

  const resetAll = () => {
    setActiveStep(0);
    setName('');
    setNameError(null);
    setStatIncreaseId('fight');
    setSelectedWeaponIds([]);
    setSelectedArmourIds([]);
    setSelectedTrickIds([]);
    setIsFree(false);
  };

  const handleClose = () => {
    resetAll();
    closeCreateCaptainModal();
  };

  const handleNext = () => {
    if (activeStep === 0 && !name.trim()) {
      setNameError('Captain name is required');
      return;
    }
    setActiveStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const handleBack = () => setActiveStep((s) => Math.max(s - 1, 0));

  const toggleWeapon = (weaponId: string) => {
    setSelectedWeaponIds((prev) =>
      prev.includes(weaponId) ? prev.filter((id) => id !== weaponId) : [...prev, weaponId]
    );
  };

  const toggleArmour = (armourId: string) => {
    setSelectedArmourIds((prev) =>
      prev.includes(armourId) ? prev.filter((id) => id !== armourId) : [...prev, armourId]
    );
  };

  const selectTrick = (trickId: string) => {
    setSelectedTrickIds((prev) =>
      prev.includes(trickId)
        ? prev.filter((id) => id !== trickId)
        : prev.length < CAPTAIN_STARTING_TRICK_COUNT
          ? [...prev, trickId]
          : prev
    );
  };

  const handleHire = () => {
    if (!name.trim() || selectedTrickIds.length !== CAPTAIN_STARTING_TRICK_COUNT) return;
    useDirtyTrackingStore.getState().queueCaptainCreate(
      name.trim(),
      statIncreaseId,
      selectedTrickIds,
      selectedWeaponIds,
      selectedArmourIds,
      isFree
    );
    resetAll();
    closeCreateCaptainModal();
  };

  const trickLimitReached = selectedTrickIds.length >= CAPTAIN_STARTING_TRICK_COUNT;

  return (
    <Dialog open={ui.createCaptainModalOpen} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
        <Typography variant="h6">Hire Captain ({CAPTAIN_HIRE_COST}gc)</Typography>

        <Stepper activeStep={activeStep} alternativeLabel>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {activeStep === 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Captain Name"
              fullWidth
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (e.target.value.trim()) setNameError(null);
              }}
              error={!!nameError}
              helperText={nameError}
            />
            <FormControl>
              <FormLabel>Stat Increase</FormLabel>
              <RadioGroup value={statIncreaseId} onChange={(e) => setStatIncreaseId(e.target.value)} row>
                {CAPTAIN_STAT_INCREASES.map((inc) => (
                  <FormControlLabel
                    key={inc.id}
                    value={inc.id}
                    control={<Radio size="small" />}
                    label={inc.label}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </Box>
        )}

        {activeStep === 1 && (
          <Box>
            <FormLabel>Select Weapons</FormLabel>
            <List dense>
              {captainWeapons.map((weapon) => (
                <SelectableItem
                  key={weapon.id}
                  primary={`${weapon.name} (${priceLabel(CAPTAIN_WEAPON_PRICES[weapon.id])})`}
                  secondary={weaponDetails(weapon)}
                  selected={selectedWeaponIds.includes(weapon.id)}
                  onClick={() => toggleWeapon(weapon.id)}
                />
              ))}
            </List>
            {warnings.map((warning) => (
              <Alert key={warning} severity="warning" sx={{ mt: 1 }}>{warning}</Alert>
            ))}
          </Box>
        )}

        {activeStep === 2 && (
          <Box>
            <FormLabel>Select Armour</FormLabel>
            <List dense>
              {captainArmours.map((armour) => (
                <SelectableItem
                  key={armour.id}
                  primary={`${armour.name} (${priceLabel(CAPTAIN_ARMOUR_PRICES[armour.id])})`}
                  secondary={armourDetails(armour)}
                  selected={selectedArmourIds.includes(armour.id)}
                  onClick={() => toggleArmour(armour.id)}
                />
              ))}
            </List>
            {warnings.map((warning) => (
              <Alert key={warning} severity="warning" sx={{ mt: 1 }}>{warning}</Alert>
            ))}
          </Box>
        )}

        {activeStep === 3 && (
          <Box>
            <FormLabel>
              Choose {CAPTAIN_STARTING_TRICK_COUNT} Tricks of the Trade ({selectedTrickIds.length}/{CAPTAIN_STARTING_TRICK_COUNT} selected)
            </FormLabel>
            <List dense>
              {tricks.map((trick: Trick) => (
                <SelectableItem
                  key={trick.id}
                  primary={trick.name}
                  secondary={`${trick.effect} — Declare: ${trick.whenToDeclare}`}
                  selected={selectedTrickIds.includes(trick.id)}
                  disabled={trickLimitReached && !selectedTrickIds.includes(trick.id)}
                  onClick={() => selectTrick(trick.id)}
                />
              ))}
            </List>
          </Box>
        )}

        <Typography variant="body2">
          Total cost: {isFree ? 0 : totalCost}gc (hire {CAPTAIN_HIRE_COST}gc + equipment {equipmentCost}gc)
          {warband ? ` — Warband gold: ${warband.gold}gc` : ''}
        </Typography>
        {isLastStep && insufficientGold && (
          <Alert severity="error">Not enough gold to hire this captain.</Alert>
        )}
      </DialogContent>
      <DialogActions>
        <FormControlLabel
          control={
            <Checkbox checked={isFree} onChange={(e) => setIsFree(e.target.checked)} size="small" />
          }
          label="Add for free"
          sx={{ mr: 'auto' }}
        />
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={handleBack} disabled={activeStep === 0}>
          Back
        </Button>
        {isLastStep ? (
          <Button
            onClick={handleHire}
            variant="contained"
            disabled={insufficientGold || selectedTrickIds.length !== CAPTAIN_STARTING_TRICK_COUNT}
          >
            Hire Captain
          </Button>
        ) : (
          <Button onClick={handleNext} variant="contained">
            Next
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
